import { useEffect, useState } from "react";
import { ExternalLink, BarChart3, Search, Youtube, MessageSquare, Pin, Globe, Check, Save, Trash2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

type Props = {
  trendId: string;
  trendTitle: string;
  tags?: string[];
};

type Outcome = "positive" | "neutral" | "negative" | "unknown";

type SavedCheck = {
  id: string;
  tool_key: string;
  tool_label: string;
  outcome: Outcome;
  notes: string | null;
  url: string | null;
  updated_at: string;
};

const OUTCOMES: { value: Outcome; label: string; cls: string }[] = [
  { value: "positive", label: "✓ Strong", cls: "border-primary/60 text-primary" },
  { value: "neutral", label: "~ Mixed", cls: "border-border text-foreground" },
  { value: "negative", label: "✗ Weak", cls: "border-destructive/60 text-destructive" },
];

export function ValidationTools({ trendId, trendTitle, tags = [] }: Props) {
  const { toast } = useToast();
  const q = encodeURIComponent(trendTitle);
  const seed = tags[0] ? encodeURIComponent(tags[0]) : q;

  const tools = [
    { key: "google_trends", label: "Google Trends", hint: "search interest over time", url: `https://trends.google.com/trends/explore?q=${q}`, Icon: BarChart3 },
    { key: "google", label: "Google search", hint: "see who already ranks", url: `https://www.google.com/search?q=${q}`, Icon: Search },
    { key: "youtube", label: "YouTube", hint: "view counts & engagement signals", url: `https://www.youtube.com/results?search_query=${q}`, Icon: Youtube },
    { key: "reddit", label: "Reddit", hint: "real audience pain points", url: `https://www.reddit.com/search/?q=${q}&sort=new`, Icon: MessageSquare },
    { key: "pinterest", label: "Pinterest", hint: "visual + lifestyle demand", url: `https://www.pinterest.com/search/pins/?q=${q}`, Icon: Pin },
    { key: "answerthepublic", label: "AnswerThePublic", hint: "question-shaped keyword ideas", url: `https://answerthepublic.com/reports/search?q=${seed}`, Icon: Globe },
    { key: "exploding_topics", label: "Exploding Topics", hint: "compare velocity vs related niches", url: `https://explodingtopics.com/search?q=${seed}`, Icon: BarChart3 },
    { key: "tally", label: "Tally · 1-min survey", hint: "send your own validation poll", url: `https://tally.so/templates/audience-research-survey`, Icon: MessageSquare },
  ];

  const [saved, setSaved] = useState<Record<string, SavedCheck>>({});
  const [openKey, setOpenKey] = useState<string | null>(null);
  const [draftNotes, setDraftNotes] = useState("");
  const [draftOutcome, setDraftOutcome] = useState<Outcome>("neutral");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await supabase
      .from("validation_checks")
      .select("id, tool_key, tool_label, outcome, notes, url, updated_at")
      .eq("trend_id", trendId);
    const map: Record<string, SavedCheck> = {};
    (data ?? []).forEach((r: any) => { map[r.tool_key] = r as SavedCheck; });
    setSaved(map);
  };

  useEffect(() => { load(); }, [trendId]);

  const openEditor = (key: string) => {
    const existing = saved[key];
    setOpenKey(openKey === key ? null : key);
    setDraftNotes(existing?.notes ?? "");
    setDraftOutcome((existing?.outcome as Outcome) ?? "neutral");
  };

  const save = async (tool: typeof tools[number]) => {
    setBusy(true);
    const { data: auth } = await supabase.auth.getUser();
    if (!auth.user) { toast({ title: "Sign in to save checks", variant: "destructive" }); setBusy(false); return; }
    const { error } = await supabase.from("validation_checks").upsert({
      user_id: auth.user.id,
      trend_id: trendId,
      tool_key: tool.key,
      tool_label: tool.label,
      outcome: draftOutcome,
      notes: draftNotes || null,
      url: tool.url,
    }, { onConflict: "user_id,trend_id,tool_key" });
    setBusy(false);
    if (error) { toast({ title: "Could not save check", description: error.message, variant: "destructive" }); return; }
    toast({ title: "Check saved" });
    setOpenKey(null);
    load();
  };

  const remove = async (key: string) => {
    const row = saved[key];
    if (!row) return;
    await supabase.from("validation_checks").delete().eq("id", row.id);
    toast({ title: "Check removed" });
    load();
  };

  const savedCount = Object.keys(saved).length;

  return (
    <details className="mt-3 rounded-lg border border-border/60 bg-secondary/20 p-3 text-sm">
      <summary className="cursor-pointer font-mono text-[10px] uppercase text-muted-foreground hover:text-foreground">
        validation tools · {tools.length} one-click checks{savedCount > 0 ? ` · ${savedCount} saved` : ""}
      </summary>
      <p className="mt-2 text-xs text-muted-foreground">
        Cross-check our AI insights with real-world demand. Save your result so you can revisit later.
      </p>
      <div className="mt-3 grid sm:grid-cols-2 gap-2">
        {tools.map((tool) => {
          const { key, label, hint, url, Icon } = tool;
          const row = saved[key];
          const isOpen = openKey === key;
          const outcomeCls =
            row?.outcome === "positive" ? "border-primary/50 bg-primary/5"
            : row?.outcome === "negative" ? "border-destructive/50 bg-destructive/5"
            : row ? "border-border bg-background" : "border-border bg-background";
          return (
            <div key={key} className={`rounded-md border ${outcomeCls} transition-colors`}>
              <div className="flex items-center justify-between gap-2 px-3 py-2 text-xs">
                <a href={url} target="_blank" rel="noopener noreferrer" className="group flex items-center gap-2 min-w-0 flex-1">
                  <Icon className="size-3.5 text-primary shrink-0" />
                  <span className="min-w-0">
                    <span className="block font-medium truncate flex items-center gap-1">
                      {label}
                      {row && <Check className="size-3 text-primary" />}
                    </span>
                    <span className="block font-mono text-[10px] text-muted-foreground truncate">
                      {row?.notes ? row.notes : hint}
                    </span>
                  </span>
                  <ExternalLink className="size-3 text-muted-foreground group-hover:text-primary shrink-0" />
                </a>
                <button
                  type="button"
                  onClick={() => openEditor(key)}
                  className="font-mono text-[10px] uppercase text-muted-foreground hover:text-foreground border border-border rounded px-1.5 py-0.5"
                >
                  {row ? "edit" : "save"}
                </button>
              </div>
              {isOpen && (
                <div className="border-t border-border/60 p-3 space-y-2 bg-background/60">
                  <div className="flex flex-wrap gap-1">
                    {OUTCOMES.map((o) => (
                      <button
                        key={o.value}
                        type="button"
                        onClick={() => setDraftOutcome(o.value)}
                        className={`font-mono text-[10px] uppercase rounded-full border px-2 py-0.5 ${
                          draftOutcome === o.value ? `${o.cls} bg-secondary` : "border-border text-muted-foreground"
                        }`}
                      >
                        {o.label}
                      </button>
                    ))}
                  </div>
                  <textarea
                    value={draftNotes}
                    onChange={(e) => setDraftNotes(e.target.value)}
                    placeholder="What did you find? e.g. steady growth past 12 months, 4 strong competitors…"
                    className="w-full rounded border border-border bg-background p-2 text-xs min-h-[60px]"
                  />
                  <div className="flex items-center justify-between gap-2">
                    {row ? (
                      <button
                        type="button"
                        onClick={() => remove(key)}
                        className="flex items-center gap-1 font-mono text-[10px] uppercase text-destructive hover:underline"
                      >
                        <Trash2 className="size-3" /> remove
                      </button>
                    ) : <span />}
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setOpenKey(null)}
                        className="font-mono text-[10px] uppercase text-muted-foreground hover:text-foreground"
                      >
                        cancel
                      </button>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => save(tool)}
                        className="flex items-center gap-1 font-mono text-[10px] uppercase text-primary-foreground bg-primary rounded px-2 py-1 disabled:opacity-50"
                      >
                        <Save className="size-3" /> save check
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </details>
  );
}
